import { Fragment, useLayoutEffect, useRef, useEffect, KeyboardEvent } from 'react';
import ReactDOM from 'react-dom';

import { useConfigContext } from '../config-wrapper';

import './modal.less';
import { useFocusTrap } from '../../hooks/use-focus-trap';
import { useMergedRef } from '../../hooks/use-merged-ref';

type ModalProps = {
  container: string;
  children: JSX.Element;
  onClose: () => void;
  styleName?: string;
  childActive: boolean;
};

/**
 * Create container for modal component.
 *
 * @param {string} selector - Additional class to add to container
 * @returns {HTMLDivElement}
 */
function createModalContainer(selector: string = 'omq'): HTMLElement {
  const container = document.createElement('div');
  container.classList.add(selector);

  return container;
}

/**
 * State captured when locking, needed to put the host page back as it was.
 */
type BodyScrollLock = {
  scrollTop: number;
  previousTop: string;
};

/**
 * Prevent background from scrolling while modal is visible.
 *
 * @param {string} className - class name of body scroll lock
 *
 * @returns {BodyScrollLock}
 */
function lockBodyScroll(className: string): BodyScrollLock {
  const body = document.body;
  if (document.documentElement == null || body == null) {
    return { scrollTop: 0, previousTop: '' };
  }

  // get current scroll position, since position change of body
  // will reset scroll position.
  const scrollTop = document.documentElement.scrollTop || body.scrollTop;

  // the page we are embedded in may set `top` itself — keep it to restore later
  const previousTop = body.style.top;

  // change body style to fixed, makes it unable to scroll
  // set inverted top position, to show background at same position
  body.classList.add(className);
  body.style.top = `-${scrollTop}px`;

  // return the captured state for later use
  return { scrollTop, previousTop };
}

/**
 * Revert changes made by lockBodyScroll.
 *
 * @param {string} className - class name of body scroll lock
 * @param {BodyScrollLock} lock - state captured by lockBodyScroll
 */
function unlockBodyScroll(className: string, lock: BodyScrollLock): void {
  const body = document.body;
  if (body == null) {
    return;
  }

  // restore body style. Assigning the captured value rather than '0px' drops the
  // inline property again when the host page had none, instead of leaving our
  // `top: 0px` on their <body> forever (server-2730).
  body.classList.remove(className);
  body.style.top = lock.previousTop;

  // restore scroll position
  body.scrollTop = lock.scrollTop;

  if (document.documentElement != null) {
    document.documentElement.scrollTop = lock.scrollTop;
  }
}

/**
 * Hook to prepare and remove modal container.
 *
 * @returns {HTMLElement}
 */
function useModalContainer(): HTMLElement {
  const { generateClassName, cssIdentifier } = useConfigContext();
  // create container & reference
  const modalRef = useRef(createModalContainer(cssIdentifier));
  const modalWrapper = modalRef.current;

  useLayoutEffect(() => {
    const body = document.body;
    let currentLock: BodyScrollLock = { scrollTop: 0, previousTop: '' };

    // add wrapper to document
    // and lock scrolling
    if (body != null) {
      body.appendChild(modalWrapper);
      currentLock = lockBodyScroll(generateClassName('modal-open'));
    }

    // remove wrapper
    // and unlock scrolling when component will be removed
    return () => {
      if (body != null) {
        // detach via the wrapper's actual parent: a host script that re-renders or
        // replaces <body> makes body.removeChild throw, and the throw used to skip
        // the unlock below, leaving the page frozen under `modal-open` (server-2730)
        if (modalWrapper.parentNode != null) {
          modalWrapper.parentNode.removeChild(modalWrapper);
        }

        unlockBodyScroll(generateClassName('modal-open'), currentLock);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalWrapper]);

  return modalWrapper;
}

/**
 * Modal component.
 * Displays children in a modal window and disables the
 * background page.
 *
 * @param {ModalProps} props - Component properties
 *
 * @returns {JSX.Element}
 */
export function Modal(props: ModalProps): JSX.Element {
  const { onClose, styleName, children } = props;
  const { generateClassName } = useConfigContext();
  const focusTrapRef = useFocusTrap(!props.childActive, onClose);
  const modalWrapper = useModalContainer();

  const classNames = generateClassName(`modal__panel ${styleName || ''} notranslate`);
  const contentRef = useRef<HTMLDivElement>();
  const mergedRef = useMergedRef(contentRef, focusTrapRef);

  // handle key events & close on escape
  const onKeyUp = (event: KeyboardEvent) => {
    if (event.which === 27 || event.key === 'Escape') {
      event.stopPropagation();
      event.preventDefault();
      onClose();
    }
  };

  // focus modal content to allow close via escape
  useEffect(() => {
    if (contentRef.current != null) {
      contentRef.current.focus();
    }
  }, []);

  return ReactDOM.createPortal(
    <Fragment>
      <div className={generateClassName('modal')} onClick={onClose} />
      <div className={classNames} ref={mergedRef} translate="no" onKeyUp={onKeyUp} tabIndex={-1}>
        {children}
      </div>
    </Fragment>,
    modalWrapper,
  );
}
